import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Redirect } from 'react-router-dom';

import ModalPortal from '../../../../components/modal-portal';
import { Modal } from 'ui-components/components/modal';
import { Checkbox } from 'ui-components/components/checkbox';
import Global from '../../../../store/global-selectors';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_INFO,
} from 'ui-components/services/notification-service';

import { refreshStoreList } from '../../../../store/seller/seller.actions';

import { ProviderInviteStatusesLowerCase } from '../../fulfillment-providers/types';
import { connectStore, getInviteStatus } from '../../api/vendor-api';
import { getAcceptErrorDetails } from './get-accept-error-details';

import messages from './messages';
import style from './style.module.less';

const AcceptInviteModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const isSubscriptionExpired = useSelector(Global.selectSellerIsSubscriptionExpired);

  const [isLoading, setIsLoading] = useAsyncSafeState(true);
  const [token, setToken] = useAsyncSafeState('');

  const [marketplaceName, setMarketplaceName] = useAsyncSafeState('');
  const [vendorName, setVendorName] = useAsyncSafeState('');
  const [vendorEmail, setVendorEmail] = useAsyncSafeState('');

  const [isValid, setIsValid] = useAsyncSafeState(false);
  const [didError, setDidError] = useAsyncSafeState(false);

  const [hasAcceptedTerms, setHasAcceptedTerms] = useAsyncSafeState(false);

  const updateNotification = (notificationId, notificationType, title, content) => {
    NotificationService.updateNotification(notificationId, {
      title,
      content,
      notificationType,
      progress: false,
      dontHide: false,
    });
  };

  const cancelInviteModal = () => {
    onClose();
    NotificationService.showNotification({
      title: <FormattedMessage {...messages.connectionCancelledTitle} />,
      content: <FormattedMessage {...messages.connectionCancelledMessage} />,
      notificationType: NOTIFICATION_TYPE_ERROR,
      progress: false,
      dontHide: true,
    });
  };

  const handleAcceptError = useCallback(
    (error, notificationId) => {
      const { title, content } = getAcceptErrorDetails(error, marketplaceName);

      updateNotification(notificationId, NOTIFICATION_TYPE_ERROR, title, content);
    },
    [marketplaceName],
  );

  const acceptInvite = useCallback(async () => {
    onClose();
    const notificationId = NotificationService.showNotification({
      title: <FormattedMessage {...messages.establishingConnectionTitle} />,
      content: <FormattedMessage {...messages.establishingConnectionMessage} values={{ marketplaceName }} />,
      notificationType: NOTIFICATION_TYPE_INFO,
      progress: true,
      dontHide: true,
    });
    try {
      await connectStore(token, { partnerName: vendorName });
      updateNotification(
        notificationId,
        NOTIFICATION_TYPE_SUCCESS,
        <FormattedMessage {...messages.succesfulConnectionTitle} />,
        <FormattedMessage {...messages.succesfulConnectionMessage} values={{ marketplaceName }} />,
      );
      history.push('/settings/stores');
      dispatch(refreshStoreList());
    } catch (error) {
      handleAcceptError(error?.response?.data, notificationId);
    }
  }, [dispatch, handleAcceptError, history, marketplaceName, onClose, token, vendorName]);

  const toggleAccept = () => {
    setHasAcceptedTerms(!hasAcceptedTerms);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setToken(urlParams.get('token'));
    getInviteStatus(urlParams.get('token'))
      .then((response) => {
        setDidError(false);
        if (response?.status.toLowerCase() === ProviderInviteStatusesLowerCase.pending) {
          setIsValid(true);
        } else {
          setIsValid(false);
        }
        setMarketplaceName(response?.sender_name);
        setVendorEmail(response?.recipient_email);
        setVendorName(response?.recipient_name);
        setIsLoading(false);
      })
      .catch(() => {
        setDidError(true);
        setIsValid(false);
        setIsLoading(false);
      });
  }, [marketplaceName, onClose, setDidError, setIsLoading, setIsValid, setMarketplaceName, setToken, setVendorEmail, setVendorName, token]);

  if (isSubscriptionExpired) {
    return <Redirect to='/settings/payments' />;
  }

  if (isLoading) {
    return null;
  }

  if (!isValid) {
    return (
      <ModalPortal onClose={onClose}>
        <Modal size='small'>
          <ModalHeader>
            {didError ? <FormattedMessage {...messages.modalHeaderError} /> : <FormattedMessage {...messages.modalHeaderNotActive} />}
          </ModalHeader>
          <ModalBody className={style['ss-settings-modal-body']}>
            <div className={style['inactive-modal-body-text']}>
              {didError ? (
                <FormattedMessage {...messages.modalErrorMessage} />
              ) : (
                <FormattedMessage
                  {...messages.inviteNotActiveBody}
                  values={{
                    marketplaceName: marketplaceName || 'marketplace',
                    vendorEmail,
                  }}
                />
              )}
            </div>
          </ModalBody>
          <ModalFooter className={style['ss-settings-modal-footer']}>
            <Button onClick={onClose} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...messages.closeButton} />
            </Button>
          </ModalFooter>
        </Modal>
      </ModalPortal>
    );
  }

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='medium'>
        <ModalHeader>
          {isValid ? <FormattedMessage {...messages.modalHeader} /> : <FormattedMessage {...messages.modalHeaderNotActive} />}
        </ModalHeader>
        <ModalBody className={style['ss-settings-modal-body']}>
          <div className={style['modal-body-text']}>
            <FormattedMessage
              {...messages.connectToMarketPlaceBody}
              values={{
                marketplaceName: marketplaceName || 'marketplace',
                vendorEmail,
                vendorName,
              }}
            />
          </div>
          <div className={style['modal-body-text']}>
            <FormattedMessage
              {...messages.connectToMarketPlaceBodyConformation}
              values={{ marketplaceName: marketplaceName || 'marketplace' }}
            />
          </div>
          <div className={style['modal-body-confirm-agreement']}>
            <label className={style['checkbox-label']}>
              <FormattedMessage {...messages.confirmCheckboxLabel} />
            </label>
            <div className={style['checkbox-container']}>
              <Checkbox checked={hasAcceptedTerms} label={<FormattedMessage {...messages.confirmCheckboxText} />} onChange={toggleAccept} />
            </div>
          </div>
        </ModalBody>
        <ModalFooter className={style['ss-settings-modal-footer']}>
          <Button onClick={cancelInviteModal} theme='tertiary'>
            <FormattedMessage {...messages.cancelButton} />
          </Button>
          <Button disabled={!hasAcceptedTerms} onClick={acceptInvite} theme='primary'>
            <FormattedMessage {...messages.connectButton} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

AcceptInviteModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default AcceptInviteModal;
